import { useAuth0 } from "@auth0/auth0-react";
import { useStripe } from "@stripe/react-stripe-js";
import StripeWrapper from "components/stripe/StripeWrapper";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";

import api, { NO_RETRY } from "api/api";
import endpoint from "api/endpoints";
import { ProcessingOverlay } from "common";
import ToastNotification from "common/toast/Toast";
import { formatCurrency } from "config/config";

import AddPaymentMethod from "./AddPaymentMethod";
import SavedPaymentMethods from "./SavedPaymentMethods";

import { Button, Dialog, Icon } from "@/design-system";

/**
 * "Pay invoice" — the design reference's PAY INVOICE DIALOG (amount due, a
 * saved-card picker, Cancel / Pay footer, lock note), wired to
 * `POST /api/stripe/invoice/pay`, which charges the invoice's OWN live balance
 * — but only if it equals `expectedAmount`, the figure on the Pay button;
 * otherwise it answers 409 + liveBalance and the dialog shows the new figure
 * for the customer to confirm. It records a NetSuite Customer Payment applied
 * to the invoice, then captures. 3DS comes back as
 * `requiresAction`; Stripe.js finishes the challenge and
 * `POST /api/stripe/invoice/pay/capture` completes the same sequence.
 *
 * Mounted by `pages/HistoryOrderDetails.jsx` per invoice row. Wrapped in
 * `StripeWrapper` because `useStripe` (for `handleNextAction`) needs the
 * Elements provider above it.
 *
 * `invoice`: `{ id, tranid, foreigntotal, amountremaining? }` from
 * `useOrderDetails().invoices`. `onPaid(result)` fires after a successful
 * capture; the parent marks that row Paid In Full (`markInvoicePaid`) and
 * re-reads only the invoice list (`refetchInvoices`).
 */
function PayInvoiceDialogInner({ invoice, stripeCustomerId, onClose, onPaid }) {
  const stripe = useStripe();
  const { getAccessTokenSilently } = useAuth0();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [hasSavedCards, setHasSavedCards] = useState(null);
  // Stable identity on purpose: SavedPaymentMethods lists this prop in its
  // fetch useCallback deps, so an inline arrow re-fetched (and re-skeletoned)
  // the card list on every render of this dialog — twice on open, on each card
  // click, on each isPaying flip (Oct 2026 review).
  const handlePaymentMethodsChange = useCallback(
    (methods) => setHasSavedCards(methods.length > 0),
    []
  );
  const [isPaying, setIsPaying] = useState(false);

  // The server answers 409 + liveBalance when NetSuite's balance no longer
  // matches what we showed; we then display the live figure and let the
  // customer confirm that one.
  const [liveBalance, setLiveBalance] = useState(null);

  // Remaining balance when the query provides it, else the invoice total.
  const amountDue =
    liveBalance ??
    (Number(invoice?.amountremaining ?? invoice?.foreigntotal) || 0);
  // Stripe's CAD minimum; the server refuses below it too.
  const belowMinimum = amountDue > 0 && amountDue < 0.5;

  const handleClose = () => {
    if (isPaying) return;
    onClose?.();
  };

  const finish = (result) => {
    ToastNotification.success(
      `Payment received — invoice ${invoice.tranid} is paid. Thank you!`
    );
    onPaid?.(result);
  };

  const handleConfirmPay = async () => {
    if (!selectedPaymentMethodId) {
      ToastNotification.error("Select or add a card to pay with.");
      return;
    }
    setIsPaying(true);
    try {
      const token = await getAccessTokenSilently();
      // Money moves on both calls below: never let axios-retry replay them.
      const requestConfig = {
        headers: { Authorization: `Bearer ${token}` },
        ...NO_RETRY,
      };
      const { data } = await api.post(
        endpoint.POST_PAY_INVOICE(),
        {
          invoiceId: invoice.id,
          paymentMethodId: selectedPaymentMethodId,
          customerId: stripeCustomerId,
          // What the button said — the server charges its live balance only
          // if it is this number, else 409s with the current one.
          expectedAmount: amountDue,
        },
        requestConfig
      );

      if (data.requiresAction && data.paymentIntent?.client_secret) {
        if (!stripe) {
          throw new Error("Payment system is still loading. Please try again.");
        }
        // Same as checkout's PaymentForm: the intent was confirmed server-side
        // with automatic confirmation, so handleNextAction (not
        // handleCardAction) runs the challenge.
        const result = await stripe.handleNextAction({
          clientSecret: data.paymentIntent.client_secret,
        });
        if (result.error) {
          throw new Error(result.error.message || "Authentication failed");
        }
        const capture = await api.post(
          endpoint.POST_PAY_INVOICE_CAPTURE(),
          { paymentIntentId: result.paymentIntent.id, invoiceId: invoice.id },
          requestConfig
        );
        finish(capture.data);
      } else if (data.success) {
        finish(data);
      } else {
        throw new Error(data.message || "Payment failed");
      }
    } catch (err) {
      if (
        err.response?.status === 409 &&
        typeof err.response.data?.liveBalance === "number"
      ) {
        setLiveBalance(err.response.data.liveBalance);
      }
      ToastNotification.error(
        err.response?.data?.message ||
          err.message ||
          "Payment failed. Please try again.",
        { autoClose: false }
      );
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <>
      {/* Same blocking "don't close this window" overlay as checkout, for the
          charge + NetSuite record. It stacks above the dialog by itself. */}
      <ProcessingOverlay
        open={isPaying}
        text="Processing payment..."
        detail={`We're securely charging your card and recording the payment for invoice ${invoice?.tranid || ""}. This may take a moment.`}
        redirectNote="The invoice will show as paid once complete."
      />
      <Dialog
        open={Boolean(invoice)}
        onClose={handleClose}
        title="Pay invoice"
        subtitle={invoice?.tranid}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isPaying}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              startIcon="lock"
              onClick={handleConfirmPay}
              loading={isPaying}
              disabled={!selectedPaymentMethodId || belowMinimum}
              title={
                belowMinimum
                  ? "This balance is below the minimum for an online card payment — please contact us."
                  : undefined
              }
            >
              Pay {formatCurrency(amountDue)}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-[14px] pt-[6px] pb-[var(--space-2)]">
          <div className="flex items-center justify-between bg-soft rounded-md py-[var(--space-4)] px-[18px]">
            <span className="text-sm text-ink-2">Amount due</span>
            <span className="font-sans text-3xl font-bold text-blue-900 tracking-[var(--tracking-tight)]">
              {formatCurrency(amountDue)}
            </span>
          </div>
          {stripeCustomerId ? (
            <div>
              <div className="flex items-center justify-between mb-[9px]">
                <div className="text-sm font-semibold">Payment method</div>
                {!showAddPaymentMethod && hasSavedCards === false && (
                  <Button
                    variant="outline"
                    size="sm"
                    startIcon="add_card"
                    onClick={() => setShowAddPaymentMethod(true)}
                  >
                    Add payment method
                  </Button>
                )}
              </div>

              {showAddPaymentMethod ? (
                <AddPaymentMethod
                  customerId={stripeCustomerId}
                  onPaymentMethodAdded={(pm) => {
                    setSelectedPaymentMethodId(pm.id);
                    setShowAddPaymentMethod(false);
                  }}
                  onCancel={() => setShowAddPaymentMethod(false)}
                />
              ) : (
                <SavedPaymentMethods
                  customerId={stripeCustomerId}
                  onSelectPaymentMethod={setSelectedPaymentMethodId}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  showTitle={false}
                  allowRemove={false}
                  onPaymentMethodsChange={handlePaymentMethodsChange}
                />
              )}
            </div>
          ) : (
            <div className="text-sm text-ink-2">
              Add a card under Account → Payment methods to pay online.
            </div>
          )}

          <div className="flex items-center gap-[7px] text-xs text-ink-3 justify-center">
            <Icon name="lock" size={15} />
            Secured by 256-bit encryption
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default function PayInvoiceDialog(props) {
  return (
    <StripeWrapper>
      <PayInvoiceDialogInner {...props} />
    </StripeWrapper>
  );
}

PayInvoiceDialog.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    tranid: PropTypes.string,
    foreigntotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amountremaining: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  stripeCustomerId: PropTypes.string,
  onClose: PropTypes.func,
  onPaid: PropTypes.func,
};
